import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import { manageTeamStore } from 'state/league/manageTeam'
import {
  CreateLeagueTeamWithNewTeamPayload,
  LeagueTeamPayload,
  LeagueTeamResp,
} from 'types/team/leagueTeam'

const getLeagueTeam = async (leagueId: string) => {
  const response = await axiosGet(`v1/league-team/league/${leagueId}`)
  return response.data
}

export const useGetLeagueTeam = (leagueId: string) => {
  const { setLeagueTeams } = manageTeamStore(store => ({
    setLeagueTeams: store.setLeagueTeams,
  }))
  const leagueTeam = useQuery<LeagueTeamResp[]>(
    ['league-team-listing'],
    () => getLeagueTeam(leagueId),
    {
      enabled: false,
      onSuccess: data => {
        setLeagueTeams(data)
      },
    },
  )
  return leagueTeam
}

const createTeamForLeague = async ({
  organizationId,
  payload,
}: {
  organizationId: string
  payload: LeagueTeamPayload
}) => {
  const response = await axiosPost(payload, `v1/league-team/${organizationId}`)
  return response.data
}

export const useCreateTeamForLeague = () => {
  const queryClient = useQueryClient()
  return useMutation(createTeamForLeague, {
    onSuccess: newTeam => {
      queryClient.setQueryData(
        ['league-team-listing'],
        (oldData: LeagueTeamResp[] | undefined) => {
          if (!oldData) return [newTeam]
          return [...oldData, newTeam]
        },
      )
    },
  })
}

// create new team and add to league
const createNewTeamForLeague = async ({
  organizationId,
  payload,
}: {
  organizationId: string
  payload: CreateLeagueTeamWithNewTeamPayload
}) => {
  const response = await axiosPost(
    payload,
    `v1/league-team/${organizationId}/with-new-team`,
  )
  return response.data
}

export const useCreateNewTeamForLeague = () => {
  const queryClient = useQueryClient()
  return useMutation(createNewTeamForLeague, {
    onSuccess: newTeam => {
      queryClient.setQueryData(
        ['league-team-listing'],
        (oldData: LeagueTeamResp[] | undefined) => {
          if (!oldData) return [newTeam]
          return [...oldData, newTeam]
        },
      )
    },
  })
}

// update league team
const updateTeamInLeague = async ({
  teamId,
  payload,
}: {
  teamId: string
  payload: LeagueTeamPayload
}) => {
  const response = await axiosPatch(payload, `v1/league-team/${teamId}`)
  return response.data
}

export const useUpdateTeamInOrganization = () => {
  const queryClient = useQueryClient()
  return useMutation(updateTeamInLeague, {
    onSuccess: updatedTeam => {
      queryClient.setQueryData(
        ['get-all-teams-list'],
        (oldData: LeagueTeamResp[] | undefined) => {
          if (!oldData) return undefined
          return oldData.map(team =>
            team._id === updatedTeam._id ? updatedTeam : team,
          )
        },
      )
    },
  })
}

// delete league team
const deleteTeamInLeague = async (leagueTeamId: string) => {
  const response = await axiosDelete(`v1/league-team/${leagueTeamId}`)
  return response.data
}
export const useDeleteTeamInLeague = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteTeamInLeague, {
    onSuccess: deletedTeam => {
      queryClient.setQueryData(
        ['league-team-listing'],
        (oldData: LeagueTeamResp[] | undefined) => {
          if (!oldData) return
          return oldData.filter(team => team._id !== deletedTeam._id)
        },
      )
    },
  })
}

// get team of league and organization
const getTeamOfLeagueAndOrganization = async (
  leagueId: string,
  organizationId: string,
) => {
  const response = await axiosGet(
    `v1/league-team/organization/${organizationId}/${leagueId}`,
  )
  return response.data
}
export const useGetTeamOfLeagueAndOrganization = (
  leagueId: string,
  organizationId: string,
) => {
  const teamOfLeagueAndOrganization = useQuery<LeagueTeamResp[]>(
    ['team-of-league-and-organization'],
    () => getTeamOfLeagueAndOrganization(leagueId, organizationId),
    {
      enabled: false,
    },
  )
  return teamOfLeagueAndOrganization
}
